import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PrimaryStepper from "../../../components/_utils/CommonSteppers/PrimaryStepper";
import TextAuthMatrixSelectTxnAmount from "../../../components/Settings/TestAuthMatrix/TestAuthMatrixSelectTxnAmount";
import TextAuthMatrixSelectUser from "../../../components/Settings/TestAuthMatrix/TestAuthMatrixSelectUser";

import {
  getTxnAuth,
  testAuthMatrix,
  testPredictedApproval,
} from "../../../redux/features/Settings/TestAuthMatrix";
import "./index.css";
import SVGComponent from "../../../components/_utils/SVGComponent";
import ICONS from "../../../assets/Icons";
import CommonToast from "../../../components/_utils/CommonToast";
import globalText from "../../../utils/globalText";
import SmallPopup from "../../../components/_utils/CommonSmallPopup";
import { GlobalConfiguration } from "../../../utils/configurationProvider";

/**
 * TestAuthMatrix - A multi-step component for testing authorization matrix rules.
 *
 * This component guides the user through a 3-step process:
 * 1. Select transaction type and enter amount
 * 2. Select users to approve the transaction
 * 3. Optionally select previous approvers
 *
 * It uses Redux for state management and React Router for navigation between
 * test steps and result pages.
 *
 */
const TestAuthMatrix = () => {
  //variables -------
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * loginData - User login data from Redux store
   * Contains entity information and authentication status
   */
  const loginData = useSelector((state) => state.refreshToken.data);

  /** steps - Labels for each step in the authorization matrix test flow */
  const steps = [
    "Select transaction type and enter the amount",
    "Select users to approve",
    "Select previous approvers (Optional)",
  ];

  //states -------
  /**
   * activeStep - Current step index (0-based)
   * Starts at 0 (first step: transaction type and amount)
   */
  const [activeStep, setActiveStep] = useState(
    GlobalConfiguration?.lengthCheckZero,
  );
  /**
   * nextButtonEnabled - Whether the Next/Test button is enabled
   * Controlled by form validation in useEffect
   */
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  /**
   * showRupeeSymbol - Whether to display the rupee symbol in amount input
   */
  const [showRupeeSymbol, setShowRupeeSymbol] = useState(false);

  /** amount - Transaction amount entered by user */
  const [amount, setAmount] = useState("");

  /** amountInWords - Amount converted to words for display */
  const [amountInWords, setAmountInWords] = useState("");

  /** txnType - Selected transaction type */
  const [txnType, setTxnType] = useState("");

  /** testPredictedAppLoading - Loading state for predicted approval API call */
  const [testPredictedAppLoading, setTestPredictedAppLoading] = useState(false);

  /** testAuthMatrixLoading - Loading state for auth matrix test API call */
  const [testAuthMatrixLoading, setTestAuthMatrixLoading] = useState(false);

  /** showConfirmLeavePopup - Whether to show confirmation dialog when leaving with unsaved changes */
  const [showConfirmLeavePopup, setShowConfirmLeavePopup] = useState(false);

  /** selectedUser - Currently selected user for approval (array with single object) */
  const [selectedUser, setSelectedUser] = useState([{}]);

  /** selectedPreviousUsersId - IDs of previously selected approvers */
  const [selectedPreviousUsersId, setSelectedPreviousUsersId] = useState([]);

  /** selectedPreviousUsers - Full objects of previously selected approvers */
  const [selectedPreviousUsers, setSelectedPreviousUsers] = useState([]);

  //functions --------
  /**
   * Handles the predicted approval API call for the authorization matrix test.
   *
   * This function calls the testPredictedApproval Redux action to check what the
   * predicted approval outcome would be based on the current transaction details
   * and existing approvals. It handles two scenarios:
   * - Initial check (page !== "outcome"): Validates rules exist and advances to next step
   * - Outcome check (page === "outcome"): Navigates to success page with prediction results
   *
   * @sideeffect
   *   - Sets testPredictedAppLoading state
   *   - Dispatches testPredictedApproval action
   *   - Navigates to success/error pages on completion
   *   - Shows error toasts on failure
   */
  const handleTestPredictedApproval = async (page) => {
    setTestPredictedAppLoading(true);
    const entityId = loginData?.status ? loginData?.entity?.entity_id : "";
    let gradeCount = {};

    selectedPreviousUsers?.forEach((val) => {
      const grade = val?.grade?.trim();
      if (grade) {
        gradeCount[grade] = gradeCount[grade]
          ? gradeCount[grade] + GlobalConfiguration?.lengthCheckOne
          : GlobalConfiguration?.lengthCheckOne;
      }
    });
    const existingApprovalsGradeCount =
      page === "outcome" ? JSON.stringify(gradeCount) : "{}";

    let response = await dispatch(
      testPredictedApproval({
        entity_id: entityId,
        txnType,
        amount,
        existing_approvals_grade_count: existingApprovalsGradeCount,
      }),
    );

    if (page === "outcome") {
      if (response?.payload?.status) {
        navigate(
          "/settings/test_authorization_matrix/test_authorization_matrix_success",
          {
            state: {
              page: "partial",
              selectedUser:
                selectedUser?.[GlobalConfiguration?.lengthCheckZero],
              selectedPreviousUsers: selectedPreviousUsers,
              predicted_approval: response?.payload?.predicted_approval,
            },
          },
        );
      } else {
        if (response?.payload?.errors?.transaction_status) {
          CommonToast({
            text:
              "Transaction status is " +
              response?.payload?.errors?.transaction_status,
            icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        } else if (
          response?.payload?.errors?.reason &&
          response?.payload?.errors?.error
        ) {
          CommonToast({
            text: response?.payload?.errors?.reason,
            icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        }
      }
    } else {
      if (response?.payload?.status) {
        setActiveStep(GlobalConfiguration?.lengthCheckOne);
      } else {
        CommonToast({
          text: "No rules found for the provided data.",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }
    }

    setTestPredictedAppLoading(false);
  };

  /**
   * Handles the main authorization matrix test API call.
   *
   * This function dispatches the testAuthMatrix action to test the complete
   * authorization flow with the selected user and previous approvers.
   * Based on the result:
   * - If fully approved: Fetches transaction auth details and navigates to success page
   * - If not fully approved: Calls handleTestPredictedApproval to show predicted outcome
   * - If error: Navigates to error page
   *
   * @sideeffect
   *   - Sets testAuthMatrixLoading state
   *   - Dispatches testAuthMatrix and getTxnAuth actions
   *   - Navigates to success/error pages based on result
   */
  const handleTestAuthMatrix = async () => {
    setTestAuthMatrixLoading(true);
    const entityId = loginData?.status ? loginData?.entity?.entity_id : "";
    const userId =
      selectedUser?.[GlobalConfiguration?.lengthCheckZero]?.user_id;
    const userGrade =
      selectedUser?.[GlobalConfiguration?.lengthCheckZero]?.grade;
    let gradeCount = {};

    selectedPreviousUsers?.forEach((val) => {
      const grade = val?.grade?.trim();
      if (grade) {
        gradeCount[grade] = gradeCount[grade]
          ? gradeCount[grade] + GlobalConfiguration?.lengthCheckOne
          : GlobalConfiguration?.lengthCheckOne;
      }
    });

    const existingApprovals = {
      grade_count: gradeCount,
      user_ids: selectedPreviousUsersId,
    };

    let response = await dispatch(
      testAuthMatrix({
        entity_id: entityId,
        txnType,
        user_id: userId,
        user_grade: userGrade,
        amount,
        existing_approvals: existingApprovals,
      }),
    );

    if (response?.payload?.status) {
      if (
        response?.payload?.transaction_auth_matrix
          ?.transaction_final_approved === true
      ) {
        const id =
          response?.payload?.transaction_auth_matrix?.authorization_rule_id;
        const res = await dispatch(getTxnAuth({ id }));

        if (res?.payload?.status) {
          navigate(
            "/settings/test_authorization_matrix/test_authorization_matrix_success",
            {
              state: {
                page: "success",
                selectedUser:
                  selectedUser?.[GlobalConfiguration?.lengthCheckZero],
                selectedPreviousUsers: selectedPreviousUsers,
                transaction_auth_matrices:
                  res?.payload?.transaction_auth_matrices,
              },
            },
          );
        }
      } else if (
        response?.payload?.transaction_auth_matrix
          ?.transaction_final_approved === false
      ) {
        handleTestPredictedApproval("outcome");
      }
    } else {
      navigate(
        "/settings/test_authorization_matrix/test_authorization_matrix_error",
        {
          state: {
            page: "error",
            selectedUser: selectedUser?.[GlobalConfiguration?.lengthCheckZero],
            selectedPreviousUsers: selectedPreviousUsers,
          },
        },
      );
    }
    setTestAuthMatrixLoading(false);
  };

  /**
   * Handles the back button click in the stepper.
   *
   * On step 0 (first step): Checks for unsaved changes and either shows
   * confirmation popup or navigates back to settings.
   * On other steps: Simply decrements the active step index.
   */

  const handleBack = () => {
    if (activeStep === GlobalConfiguration?.lengthCheckZero) {
      if (isFormDirty()) {
        setShowConfirmLeavePopup(true);
      } else {
        navigate("/settings");
      }
    } else {
      setActiveStep((prev) => prev - GlobalConfiguration?.lengthCheckOne);
    }
  };

  /**
   * Handles the next/test button click in the stepper.
   *
   * Behavior depends on the current active step:
   * - Step 0: Calls handleTestPredictedApproval for initial validation
   * - Step 1: Advances to step 2 (previous approvers selection)
   * - Step 2: Calls handleTestAuthMatrix for final authorization test
   *
   */
  const handleNext = () => {
    if (activeStep === GlobalConfiguration?.lengthCheckZero) {
      handleTestPredictedApproval();
    } else if (activeStep === GlobalConfiguration?.lengthCheckOne) {
      setActiveStep((prev) => prev + GlobalConfiguration?.lengthCheckOne);
    } else if (activeStep === GlobalConfiguration?.lengthCheckTwo) {
      handleTestAuthMatrix();
    }
  };

  //useEffects --------
  /**
   * Controls the enabled state of the Next/Test button based on current step and form data.
   *
   * Step 0 (transaction type & amount): Enabled when txnType, amount are set and amount != "0"
   * Step 1 (select user): Enabled when a user is selected
   * Step 2 (previous approvers): Always enabled (optional step)
   * Otherwise: Disabled
   */
  useEffect(() => {
    if (
      activeStep === GlobalConfiguration?.lengthCheckZero &&
      txnType !== "" &&
      amount !== "" &&
      amount !== "0"
    ) {
      setNextButtonEnabled(true);
    } else if (
      (activeStep === GlobalConfiguration?.lengthCheckOne &&
        selectedUser?.[GlobalConfiguration?.lengthCheckZero]?.id) ||
      activeStep === GlobalConfiguration?.lengthCheckTwo
    ) {
      setNextButtonEnabled(true);
    } else {
      setNextButtonEnabled(false);
    }
  }, [
    activeStep,
    txnType,
    amount,
    selectedUser,
    selectedPreviousUsersId,
    selectedPreviousUsers,
  ]);

  // stepper config
  /**
   * Defines colors, text, and hover states for previous/next buttons
   */
  const commonStepperConfig = {
    previousEnableBgColor: "var(--primary-nav-act)",
    previousDisableBgColor: "var(--primary-nav-act)",
    previousEnableTextColor: "var(--text-primary)",
    nextEnableBgColor: "var(--text-primary)",
    nextDisableBgColor: "var(--text-disabled)",
    nextEnableTextColor: "var(--primary-bg)",
    nextDisableTextColor: "var(--text-primary)",
    previousHoverOption: "white",
    nextHoverOption: "black",
  };

  /**
   * Checks if the form has unsaved changes (dirty state).
   *
   */
  const isFormDirty = () => {
    return txnType !== "" || amount !== "";
  };

  /**
   * Maps each step to its specific button configuration:
   * - Step 0: "Cancel"/"Next" with predicted approval loader
   * - Step 1: "Back"/"Next" no loader
   * - Step 2: "Back"/"Test" with auth matrix loader, green "Test" button
   */
  const dataArray = steps.map((stepLabel, index) => ({
    ...commonStepperConfig,
    // Last step gets positive (green) next button
    ...(index === steps.length - GlobalConfiguration?.lengthCheckOne && {
      nextEnableBgColor: "var(--positive-act)",
      nextEnableTextColor: "var(--text-primary)",
    }),
    previousText:
      index === GlobalConfiguration?.lengthCheckZero ? "Cancel" : "Back",
    nextText:
      index === GlobalConfiguration?.lengthCheckZero
        ? "Next"
        : index === GlobalConfiguration?.lengthCheckOne
          ? "Next"
          : "Test",
    nextLoaderNeeded:
      index === GlobalConfiguration?.lengthCheckZero ||
      index === GlobalConfiguration?.lengthCheckTwo,
    nextIsLoading:
      index === GlobalConfiguration?.lengthCheckZero
        ? testPredictedAppLoading
        : index === GlobalConfiguration?.lengthCheckTwo
          ? testAuthMatrixLoading
          : false,
    nextIsEnabled: nextButtonEnabled,
    stepperText: stepLabel,
  }));

  return (
    <>
      {showConfirmLeavePopup && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopup
            headerTitle={globalText?.LEAVE_PAGE_WARNING_HEADER}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-lg"
            subtext={globalText?.EXIT_CURRENT_FLOW_NOTE}
            btnOneText={globalText?.STAY}
            btnOneHandleFunction={() => setShowConfirmLeavePopup(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText={globalText?.LEAVE}
            btnTwoHandleFunction={() => {
              setShowConfirmLeavePopup(false);
              navigate("/settings");
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
          />
        </div>
      )}
      <div className="w-full-width h-full-height bg-primary-bg overflow-y-hidden flex flex-col">
        <div className="hidden Qsmd:flex min-h-navbar-breadcrumb-height Qlg:bg-transparent bg-primary-nav"></div>
        <div className="test-auth-stepper-main-div w-width-full z-z-index-header relative min-h-stepper-height">
          <div className="w-width-full Qlg:w-max-content-width m-auto">
            <PrimaryStepper
              activeStep={activeStep}
              handleForwardAction={handleNext}
              handleBackwardAction={handleBack}
              dataArray={dataArray}
            />
          </div>
        </div>
        <div className="test-auth-pages-container-div flex-1">
          <div className="test-auth-resp-header shadow-box-shadow-bottom">
            <div
              className="test-auth-resp-header-img"
              onClick={() => {
                if (isFormDirty()) {
                  setShowConfirmLeavePopup(true);
                } else {
                  navigate("/settings");
                }
              }}
            >
              <SVGComponent
                url={ICONS?.ARROW_LEFT_WHITE}
                height={"var(--icon-sm)"}
                width={"var(--icon-sm)"}
                color={"var(--text-primary)"}
              />
            </div>
            <div className="test-auth-resp-header-text">
              <span className="text-base text-text-primary">
                Test authorization matrix
              </span>
              <span className="text-xs text-text-secondary font-semibold">
                {steps[activeStep]}
              </span>
            </div>
          </div>
          <div className="test-auth-pages-main-div Qsmd:px-padding-base">
            {activeStep === GlobalConfiguration?.lengthCheckZero ? (
              <TextAuthMatrixSelectTxnAmount
                showRupeeSymbol={showRupeeSymbol}
                setShowRupeeSymbol={setShowRupeeSymbol}
                amount={amount}
                setAmount={setAmount}
                txnType={txnType}
                setTxnType={setTxnType}
                amountInWords={amountInWords}
                setAmountInWords={setAmountInWords}
              />
            ) : activeStep === GlobalConfiguration?.lengthCheckOne ? (
              <TextAuthMatrixSelectUser
                pageType={"users"}
                activeStep={activeStep}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                setSelectedPreviousUsers={setSelectedPreviousUsers}
              />
            ) : (
              <TextAuthMatrixSelectUser
                hMatrixSelectUser
                pageType={"previousUsers"}
                activeStep={activeStep}
                selectedUser={selectedUser}
                selectedPreviousUsersId={selectedPreviousUsersId}
                setSelectedPreviousUsersId={setSelectedPreviousUsersId}
                selectedPreviousUsers={selectedPreviousUsers}
                setSelectedPreviousUsers={setSelectedPreviousUsers}
              />
            )}
          </div>
          <div className="min-h-stepper-height"></div>
        </div>
      </div>
    </>
  );
};

export default TestAuthMatrix;
